import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import pluralize from 'pluralize'
import { useMemo } from 'react'

import {
  BulkGift,
  CrowdFund,
  Gift,
  Monthly,
  OneOff,
  AddPlants
} from 'src/components/atoms/Icons'
import { InternalLink } from 'src/components/atoms/Link'
import { Truncated } from 'src/components/atoms/Truncated'

import {
  viewProfilePath,
  viewProjectPath,
  viewRegistryPath
} from 'src/config/paths'
import { PurchaseType } from 'src/types/purchase'
import { UserEventType } from 'src/types/user'
import { numberInWords } from 'src/utils/common'

dayjs.extend(relativeTime)
dayjs.extend(utc)

export interface ActivityFeedItemProps {
  name: string
  uri?: string
  publicMessage?: string
  registryName?: string | null
  registryUri?: string | null
  createdOn: string
  quantity: number
  type?: number | null
  regionName?: string | null
  projectName?: string | null
  projectUri?: string | null
  focussed?: boolean
  purchaseType?: PurchaseType | null
  hasSubscriptionId?: boolean | null
}

type ActivityActionType =
  | 'planted'
  | 'oneOff'
  | 'monthly'
  | 'gift'
  | 'bulkGift'
  | 'crowdFund'

const getActivityTitle = (quantity: number, activityAction: string) =>
  `${numberInWords(quantity)} ${pluralize('tree', quantity)} ${pluralize(
    'was',
    quantity
  )} ${activityAction}`

const activityDetailMap: {
  [key in ActivityActionType]: {
    title: string | ((quantity: number) => string)
    icon: JSX.Element
    actionType?: string
  }
} = {
  planted: {
    icon: <AddPlants className='text-white w-10 h-10' />,
    title: quantity => getActivityTitle(quantity, 'planted')
  },
  oneOff: {
    icon: <OneOff className='text-white w-10 h-10' />,
    title: quantity => getActivityTitle(quantity, 'donated')
  },
  monthly: {
    icon: <Monthly className='text-white w-10 h-10' />,
    title: quantity => getActivityTitle(quantity, 'donated'),
    actionType: 'Monthly'
  },
  gift: {
    icon: <Gift className='text-white w-10 h-10' />,
    title: quantity => getActivityTitle(quantity, 'gifted'),
    actionType: 'Gift'
  },
  bulkGift: {
    icon: (
      <BulkGift className='text-white w-10 h-10 translate-x-1.5 -translate-y-1' />
    ),
    title: quantity => getActivityTitle(quantity, 'gifted'),
    actionType: 'Bulk gift'
  },
  crowdFund: {
    icon: <CrowdFund className='text-white w-10 h-10' />,
    title: quantity => getActivityTitle(quantity, 'donated'),
    actionType: 'Tree registry'
  }
}

export const ActivityFeedItem = ({
  name,
  uri,
  publicMessage,
  registryName,
  registryUri,
  createdOn,
  quantity,
  type,
  regionName,
  projectName,
  projectUri,
  focussed = false,
  purchaseType,
  hasSubscriptionId
}: ActivityFeedItemProps) => {
  const activityDetail = useMemo(() => {
    switch (true) {
      case type === UserEventType.Planted:
        return activityDetailMap.planted
      case purchaseType === PurchaseType.FundedTree && hasSubscriptionId:
        return activityDetailMap.monthly
      case purchaseType === PurchaseType.FundedTree:
        return activityDetailMap.oneOff
      case purchaseType === PurchaseType.GiftedTree:
        return activityDetailMap.gift
      case purchaseType === PurchaseType.BulkGifted:
        return activityDetailMap.bulkGift
      case purchaseType === PurchaseType.RegistryGift:
        return activityDetailMap.crowdFund
      default:
        return null
    }
  }, [type, purchaseType, hasSubscriptionId])

  const focusClass = useMemo(() => {
    if (focussed) return 'animate-highlight-user-event'
    return ''
  }, [focussed])

  if (!activityDetail) return null
  return (
    <div className={`flex items-start py-4 ${focusClass}`}>
      <div className='shrink-0 bg-mostlyGreen p-[10px] rounded-full h-[60px]'>
        {activityDetail?.icon}
      </div>
      <div className='ml-4 text-shark'>
        <span className='text-lg font-bold'>
          {typeof activityDetail.title === 'function'
            ? activityDetail?.title(quantity)
            : activityDetail?.title}
        </span>
        {publicMessage && publicMessage !== '' ? (
          <Truncated>
            <p className='text-lg italic'>&quot;{publicMessage}&quot;</p>
          </Truncated>
        ) : (
          ''
        )}
        <p>
          {type === 8 && projectUri ? (
            <span>
              in{' '}
              <InternalLink
                href={viewProjectPath({ routeParams: { uri: projectUri! } })}
              >
                <span className='text-base'>{projectName}</span>
              </InternalLink>{' '}
              - {regionName} region
            </span>
          ) : (
            ''
          )}
          {type === 8 && !projectUri ? (
            <span>in the {regionName} region </span>
          ) : (
            ''
          )}

          {name !== '' && name !== 'Anonymous' && uri ? (
            <span>
              by{' '}
              <InternalLink href={viewProfilePath({ routeParams: { uri } })}>
                <span className='text-base'>{name}</span>
              </InternalLink>
            </span>
          ) : (
            ''
          )}
          {name !== '' && name !== 'Anonymous' && !uri ? (
            <span className='text-base'>by {name}</span>
          ) : (
            ''
          )}
          {registryName && registryUri ? (
            <span className='text-base'>
              {' '}
              through the{' '}
              <InternalLink
                href={viewRegistryPath({ routeParams: { registryUri } })}
              >
                {registryName}
              </InternalLink>{' '}
              crowdfund
            </span>
          ) : (
            ''
          )}
        </p>
        <p className='text-sm mt-1 font-semibold text-steelGrey'>
          {activityDetail.actionType ? `${activityDetail.actionType} - ` : null}
          {dayjs().to(dayjs.utc(createdOn))}
        </p>
      </div>
    </div>
  )
}
